import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles } from 'lucide-react';
import { Button, type EditableStepPatch } from '@bliks-io/ui';
import type { ProcessDraftStep } from '@bliks-io/contracts';
import { useGenerateDraftStepSummary } from '@/api/mutations/useGenerateDraftStepSummary';
import { toastApiError } from '@/api/mutations/toast-api-error';

interface Props {
  step: ProcessDraftStep;
  processId: string;
  onPatch: (patch: EditableStepPatch) => Promise<void>;
}

/**
 * Header affordance that generates a manual draft step's name + description
 * with AI. Disabled until the step has something to summarise: without inputs,
 * rules or task text the prompt has only process context to draw from and
 * yields generic output.
 */
export function DraftStepAiNameButton({ step, processId, onPatch }: Props) {
  const { t } = useTranslation();
  const generateMutation = useGenerateDraftStepSummary();

  const hasStepContent =
    (step.inputs?.length ?? 0) > 0 ||
    (step.rules?.length ?? 0) > 0 ||
    !!step.task?.taskDescription?.trim() ||
    !!step.task?.output?.trim();

  const handleGenerate = useCallback(async () => {
    try {
      const result = await generateMutation.mutateAsync({ processId, stepId: step._id });
      await onPatch({ generatedName: result.name, generatedDescription: result.description });
    } catch (error) {
      toastApiError(error, 'processDraft.editor.generateWithAiError');
    }
  }, [generateMutation, processId, step._id, onPatch]);

  return (
    <Button
      variant="ghost"
      size="small"
      onClick={() => void handleGenerate()}
      disabled={generateMutation.isPending || !hasStepContent}
      aria-label={t('processDraft.editor.generateWithAi')}
      title={
        hasStepContent
          ? t('processDraft.editor.generateWithAi')
          : t('processDraft.editor.generateWithAiDisabled')
      }
      className="p-1! h-6! w-6! shrink-0"
    >
      {generateMutation.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
      ) : (
        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
      )}
    </Button>
  );
}
